import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useAddOrderNote } from '../hooks/use-order-mutations';
import {
  orderNoteSchema,
  type OrderNoteFormInput,
  type OrderNoteFormOutput,
} from '../schemas/order-note-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['message', 'visibleToCustomer']);

export interface AddNoteDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A timeline note, internal by default. */
export function AddNoteDialog({ storeId, order, open, onOpenChange }: AddNoteDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const addNote = useAddOrderNote(storeId);
  const form = useForm<OrderNoteFormInput, unknown, OrderNoteFormOutput>({
    resolver: useI18nResolver(orderNoteSchema),
    defaultValues: { message: '', visibleToCustomer: false },
  });

  const close = () => {
    onOpenChange(false);
    addNote.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await addNote.mutateAsync({ id: order.id, body: values });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('orders:addNote.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:addNote.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:addNote.message')}</FormLabel>
                  <FormControl>
                    <Textarea rows={4} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="visibleToCustomer"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>{t('orders:addNote.visibleToCustomer')}</FormLabel>
                    <FormDescription>{t('orders:addNote.visibleHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={addNote.isPending}>
                {addNote.isPending ? t('orders:addNote.submitting') : t('orders:addNote.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
